import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft,
  Activity,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Download,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getScoreColor = (percentage) => {
  if (percentage <= 25) return { bg: 'bg-green-100', text: 'text-green-700', bar: 'bg-green-500' };
  if (percentage <= 50) return { bg: 'bg-yellow-100', text: 'text-yellow-700', bar: 'bg-yellow-500' };
  if (percentage <= 75) return { bg: 'bg-orange-100', text: 'text-orange-700', bar: 'bg-orange-500' };
  return { bg: 'bg-red-100', text: 'text-red-700', bar: 'bg-red-500' };
};

const getScoreLabel = (percentage) => {
  if (percentage <= 25) return 'Excelente';
  if (percentage <= 50) return 'Bueno';
  if (percentage <= 75) return 'Atención';
  return 'Crítico';
};

const HealthResultsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const basePath = `/${user?.user_type || 'client'}`;
  const isClient = user?.user_type === 'client';
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (location.state?.scores) {
      setResults(location.state.scores);
      setLoading(false);
    } else {
      fetchLatestResults();
    }
  }, [location.state]);

  const fetchLatestResults = async () => {
    try {
      const response = await axios.get(`${API}/health-tests/my-tests`);
      if (response.data.length > 0) {
        const latest = response.data[0];
        setResults({
          sectionScores: latest.scores,
          totalScore: latest.total_score,
          maxScore: latest.max_score,
          percentage: latest.percentage
        });
      }
    } catch (error) {
      console.error('Error fetching results:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  if (!results) {
    return (
      <div className={`space-y-6 animate-fade-in ${isClient ? 'max-w-2xl mx-auto' : ''}`}>
        {isClient && (
          <div className="pt-1">
            <button
              onClick={() => navigate(`${basePath}/health-tests`)}
              className="flex items-center gap-1.5 text-sm text-emerald-600 font-semibold mb-3 hover:gap-2.5 transition-all duration-200"
              data-testid="back-to-health"
            >
              <ArrowLeft size={16} /> Volver a Test de Salud
            </button>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Historial de Encuestas
            </h1>
            <p className="text-sm text-gray-400 mt-1 font-medium">Tus encuestas de salud previas</p>
          </div>
        )}
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="py-12 text-center">
            <Activity size={48} className="mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No hay resultados disponibles</h2>
            <p className="text-gray-500 mb-4">Completa una evaluaci&oacute;n de salud para ver tus resultados</p>
            <Button 
              onClick={() => navigate(`${basePath}/health-tests-list`)}
              className="bg-[#467322] hover:bg-[#3A601C] text-white"
            >
              Ver Evaluaciones
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const overallColor = getScoreColor(results.percentage);
  const overallLabel = getScoreLabel(results.percentage);

  // Ordenar secciones por porcentaje (de mayor a menor - más problemáticas primero)
  const sortedSections = Object.entries(results.sectionScores || {})
    .filter(([name]) => name !== 'Comentarios Adicionales')
    .sort((a, b) => (b[1].percentage || 0) - (a[1].percentage || 0));

  return (
    <div className={`space-y-6 animate-fade-in ${isClient ? 'max-w-2xl mx-auto' : ''}`} data-testid="health-results-page">
      {/* Header */}
      {isClient ? (
        <div className="pt-1">
          <button
            onClick={() => navigate(`${basePath}/health-tests`)}
            className="flex items-center gap-1.5 text-sm text-emerald-600 font-semibold mb-3 hover:gap-2.5 transition-all duration-200"
            data-testid="back-to-health"
          >
            <ArrowLeft size={16} /> Volver a Test de Salud
          </button>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Historial de Encuestas
          </h1>
          <p className="text-sm text-gray-400 mt-1 font-medium">Análisis detallado de tu evaluación de salud</p>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate(`${basePath}/health-tests`)}
              className="border-[#467322] text-[#467322]"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Resultados Test de Salud Física
              </h1>
              <p className="text-gray-600 mt-1">Análisis detallado de tu evaluación de salud</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`${basePath}/health-tests-list`)}
              className="border-[#467322] text-[#467322]"
            >
              <RefreshCw size={18} className="mr-2" />
              Ver Evaluaciones
            </Button>
          </div>
        </div>
      )}

      {/* Overall Score */}
      <Card className="bg-[#467322] border-none overflow-hidden">
        <CardContent className="p-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="p-6 bg-white/20 rounded-2xl">
                <Activity className="text-white" size={48} />
              </div>
              <div>
                <p className="text-white/80 text-lg">Puntuación General</p>
                <span className="text-6xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {results.totalScore}
                </span>
              </div>
            </div>
            
            <div className="text-center md:text-right">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${overallColor.bg}`}>
                {results.percentage <= 50 ? (
                  <CheckCircle className={overallColor.text} size={20} />
                ) : (
                  <AlertTriangle className={overallColor.text} size={20} />
                )}
                <span className={`font-bold ${overallColor.text}`}>{overallLabel}</span>
              </div>
              <p className="text-white/60 text-sm mt-2">
                Menor puntuación = Mejor salud
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Interpretation Guide */}
      <Card className="bg-white border border-[#467322]/20">
        <CardHeader>
          <CardTitle className="text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Guía de Interpretación
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="font-semibold text-green-700">0-25%</span>
              </div>
              <p className="text-sm text-green-600">Excelente - Sin síntomas significativos</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="font-semibold text-yellow-700">26-50%</span>
              </div>
              <p className="text-sm text-yellow-600">Bueno - Algunos síntomas leves</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <span className="font-semibold text-orange-700">51-75%</span>
              </div>
              <p className="text-sm text-orange-600">Atención - Síntomas moderados</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="font-semibold text-red-700">76-100%</span>
              </div>
              <p className="text-sm text-red-600">Crítico - Requiere atención médica</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section Results */}
      <Card className="bg-white border border-[#467322]/20">
        <CardHeader>
          <CardTitle className="text-[#467322] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <TrendingUp size={24} />
            Resultados por Sección
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {sortedSections.map(([sectionName, data], index) => {
              const color = getScoreColor(data.percentage || 0);
              const label = getScoreLabel(data.percentage || 0);
              
              return (
                <div key={sectionName} className="p-4 border border-[#467322]/10 rounded-lg hover:bg-[#F2F7F0] transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 flex items-center justify-center bg-[#467322]/10 rounded-full text-sm font-bold text-[#467322]">
                        {index + 1}
                      </span>
                      <span className="font-semibold text-black">{sectionName}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={`${color.bg} ${color.text} border-none`}>
                        {label}
                      </Badge>
                      <span className="font-bold text-lg">
                        {data.score || 0}
                      </span>
                    </div>
                  </div>
                  <div className="relative">
                    <Progress 
                      value={data.percentage || 0} 
                      className="h-3"
                    />
                    <span className="absolute right-0 -top-1 text-sm font-medium text-gray-600">
                      {data.percentage || 0}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Areas of Concern */}
      {sortedSections.filter(([_, data]) => (data.percentage || 0) > 50).length > 0 && (
        <Card className="bg-amber-50 border border-amber-200">
          <CardHeader>
            <CardTitle className="text-amber-700 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <AlertTriangle size={24} />
              Áreas que Requieren Atención
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-amber-600 mb-4">
              Las siguientes áreas muestran síntomas significativos. Considera consultar con un profesional de salud.
            </p>
            <div className="flex flex-wrap gap-2">
              {sortedSections
                .filter(([_, data]) => (data.percentage || 0) > 50)
                .map(([sectionName, data]) => (
                  <Badge key={sectionName} className="bg-amber-100 text-amber-700 border-amber-300">
                    {sectionName}: {data.percentage}%
                  </Badge>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      <Card className="bg-white border border-[#467322]/20">
        <CardHeader>
          <CardTitle className="text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Recomendaciones
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle className="text-[#467322] mt-1 flex-shrink-0" size={20} />
              <span>Comparte estos resultados con tu profesional de salud en tu próxima consulta.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="text-[#467322] mt-1 flex-shrink-0" size={20} />
              <span>Realiza este test periódicamente para monitorear cambios en tu salud.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="text-[#467322] mt-1 flex-shrink-0" size={20} />
              <span>Presta especial atención a las áreas con porcentajes altos.</span>
            </li>
            {results.percentage > 50 && (
              <li className="flex items-start gap-3">
                <AlertTriangle className="text-amber-600 mt-1 flex-shrink-0" size={20} />
                <span className="text-amber-700">
                  Tu puntuación general sugiere que deberías considerar una evaluación médica profesional.
                </span>
              </li>
            )}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default HealthResultsPage;
